import React from 'react';

import { store, useAppDispatch, useAppSelector } from '@/store';
import {
  JOBS_STALL_CHECK_MS,
  activeJobs,
  anyJobStalled,
  fetchCreativeJobs,
  selectHasActiveCreativeJobs,
  type CreativeJob,
} from '@/store/slices/creativeJobs';

import { useRefetchOnVisible } from './useRefetchOnVisible';

/** A LOCAL spark-creatives behind the dev proxy (VITE_CREATIVES_PROXY_TARGET): its owner-hub stand-in only logs
 *  spark's /internal/ws/send pushes, so no progress ever reaches the browser there and the list is polled instead.
 *  Statically false in every build, so deployed environments (test, production) never poll. */
const POLL_LOCAL_SPARK = import.meta.env.DEV && Boolean(import.meta.env.VITE_CREATIVES_PROXY_TARGET);
const LOCAL_POLL_MS = 10_000;
/** A tab return fires both visibilitychange and focus: re-reads closer together than this are one. */
const REREAD_MIN_GAP_MS = 5_000;

/**
 * Keeps the creative-jobs slice hydrated for the signed-in owner, app-wide (mounted once in the layout, so a job
 * stays tracked across page navigation and with no editor open).
 *
 * Job progress arrives as `creative_job` events on the owner-hub websocket, applied centrally in `store/ws/ws.js`,
 * which also rehydrates from `GET /creatives/jobs` on socket (re)connect. This hook covers what the socket cannot:
 * - the Studio mounting while the socket is already connected (no fresh `connect`), and a token / identity change
 *   (the refresh-on-401 rotation or an impersonation switch), where the jobs are re-pulled for the right owner;
 * - pushes are best-effort: while any job is in flight, the list is re-read once the store has been silent for a
 *   full stall window (a dropped push, above all a final `done`; see jobsStalled) and when the tab comes back to the
 *   foreground (a background tab's socket can be throttled or suspended);
 * - a local spark behind the dev proxy never pushes, so there the list is polled every 10s while a job is in flight.
 *
 * `fetchCreativeJobs` no-ops when the creatives service is unconfigured, so this is safe to run app-wide.
 */
export const useCreativeJobsStream = (): void => {
  const dispatch = useAppDispatch();
  // Key the effect on the store token (not a one-time read) so a rotated token or an impersonation
  // identity change re-pulls the jobs for the now-current owner.
  const token = useAppSelector((state) => state.authentication.access_token) || '';
  const anyActive = useAppSelector(selectHasActiveCreativeJobs);
  const watching = Boolean(token) && anyActive;

  React.useEffect(() => {
    if (!token) return;
    void dispatch(fetchCreativeJobs());
  }, [dispatch, token]);

  // When EACH job last changed (a push or a re-read replaces that job's object; untouched jobs keep their identity),
  // and when this hook last re-read the list. Per job, so one job's pushes never hide another's dropped `done`.
  // Followed through a store subscription rather than a selector, so the layout does not re-render on every push.
  const changedAtRef = React.useRef<Record<string, number>>({});
  const rereadAtRef = React.useRef(0);
  React.useEffect(() => {
    const stamp = (byId: Record<string, CreativeJob>, prevById?: Record<string, CreativeJob>): void => {
      const now = Date.now();
      for (const id of Object.keys(byId)) if (byId[id] !== prevById?.[id]) changedAtRef.current[id] = now;
    };
    let prev = store.getState().creativeJobs.byId;
    stamp(prev);
    return store.subscribe(() => {
      const next = store.getState().creativeJobs.byId;
      if (next !== prev) {
        stamp(next, prev);
        prev = next;
      }
    });
  }, []);

  const reread = React.useCallback((): void => {
    const now = Date.now();
    if (now - rereadAtRef.current < REREAD_MIN_GAP_MS) return;
    rereadAtRef.current = now;
    void dispatch(fetchCreativeJobs());
  }, [dispatch]);

  React.useEffect(() => {
    if (!watching) return undefined;
    const id = window.setInterval(
      () => {
        const inFlight = activeJobs(Object.values(store.getState().creativeJobs.byId));
        const changedAts = inFlight.map((j) => changedAtRef.current[j.id] ?? 0);
        if (POLL_LOCAL_SPARK || anyJobStalled(Date.now(), changedAts, rereadAtRef.current)) reread();
      },
      POLL_LOCAL_SPARK ? LOCAL_POLL_MS : JOBS_STALL_CHECK_MS,
    );
    return () => window.clearInterval(id);
  }, [watching, reread]);

  useRefetchOnVisible(reread, watching && !POLL_LOCAL_SPARK);
};

export default useCreativeJobsStream;
